/**
 * configを変更する際 /web_app/apps/stailer-external-admin/apps/configs/*.ts にある
 * 外部管理画面v2のconfigも同時に変更してください。
 */

import {
  ServiceSpecification,
  ServiceSpecificationSchema,
} from '@service_specification/specification_commonjs/stailer_service_specification/v1/service_specification_pb'

import { EcSite } from '@apis/type/ec_site_pb'
import { fromJson, type JsonValue } from '@bufbuild/protobuf'
import * as specificationJsonProvider from '@service_specification/specification_json_provider'
import { createPartnerConfigFeatureFlagsAccountPart } from '~/models/feature_flags/account'
import { createPartnerConfigFeatureFlagsCustomerOrderPart } from '~/models/feature_flags/customer_order'
import { createPartnerConfigFeatureFlagsDeliveryMethodPart } from '~/models/feature_flags/delivery_method'
import { createPartnerConfigFeatureFlagsFrestaPart } from '~/models/feature_flags/fresta'
import { createPartnerConfigFeatureFlagsMarketingPart } from '~/models/feature_flags/marketing'
import { createPartnerConfigFeatureFlagsMerchandisingPart } from '~/models/feature_flags/merchandising'
import { createPartnerConfigFeatureFlagsPaymentPart } from '~/models/feature_flags/payment'
import { createPartnerConfigFeatureFlagsPickPackPart } from '~/models/feature_flags/pick_pack'
import { createPartnerConfigFeatureFlagsSalesReportPart } from '~/models/feature_flags/sales_report'
import { createPartnerConfigFeatureFlagsCustomerSupportPart } from '~/models/feature_flags/customer_support'

export type ProductCodeDisplayType = 'none' | 'scanCode' | 'productId'
export type ProductCodePosition = 'productNameColumn' | 'productCodeColumn'
export type SalesBadge = {
  value: number
  label: String
}

export enum ReceiptComment {
  Alchol = '20歳未満の酒類の販売は固くお断りしております。',
  SelfMedication = '●印を記した商品はセルフメディケーション税制対象商品です。',
  BundleMixMatch = '■印を記した商品はまとめ割・よりどり割適用商品です。',
  ReducedTaxRate = '※ [外税8%]または[内税8%]と記載の商品は軽減税率適用商品です。',
  BankAccountTransfer = '※ お支払い方法が口座振替のお客様は、一ヶ月分まとめて引落しになります。',
  Point = '※ ポイント加算はお届け日の1〜2日後です。',
  ContactInfo = 'お問い合わせ先：ネットスーパーアプリまたはWebサイトのお問い合わせフォームよりご連絡ください。',
}

interface FeatureFlags {
  // 代引き手数料を有効にするかどうか
  enableCashOnDeliveryFee: boolean
  // 酒類販売管理者標識を管理画面で編集できるかどうか
  // 商品マスタで酒類販売管理者標識を管理する場合はfalseにして管理画面で編集できなくする
  // stailer-serverのkeepExisitngAlcoholSalesManagementInfoと対応する
  enableEditAlcoholSalesManagementInfo: boolean
  // Stailer標準の売上連携を使うかどうか
  enableStailerStandardSalesFinalizer: boolean
  // ピッキング伝票で選択するデフォルトの日付を前日ピッキングのために翌日にする
  preferDayBeforePicking: boolean
  // 注文詳細に受け渡し日の変更を表示するかどうか	(ライフのみ)管理画面の注文を別日に移せる機能。
  // 商品金額変わったりする課題が整理されていないので、まだ使いたくない。再配達のときは使わない
  enableChangeDeliveryTime: boolean
  // ピッキング伝票にバーコードを表示するかどうか
  displayBarcodeInPickList: boolean
  // ピッキング伝票にproductCodeを表示するかどうか
  displayProductCodeInPickList: boolean
  // ピッキング伝票に表示するproductCodeの種別
  productCodeDisplayTypeInPickList: ProductCodeDisplayType
  // ピッキング伝票に表示するproductCodeの位置
  productCodePositionInPickList: ProductCodePosition
  // ピッキング伝票にproductCodeを表示する場合に下何桁までを強調表示するか
  productCodeEmphasizedDigitInPickList: number
  // サブスクリプション機能が有効かどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableSubscriptionV2: boolean
  // ポイントの分割表示をするか（ライフはボーナスポイント、通常ポイントがある）
  isDisplaySplitEarnedPoint: boolean
  // ポイント連携をしているか
  enableLinkPointInfo: boolean
  // ポイント利用ができるかどうか
  enableRedeemPoint: boolean
  // 来店宅配
  useInStoreArrangedDelivery: boolean
  // 代替品のピッキングを有効にするか
  useAlternativePicking: boolean
  // 問い合わせの管理機能の何を使うか（閲覧のみ、ステータス変更機能）
  // refs https://github.com/10xinc/stailer/issues/6816
  contactManagementType: 'viewOnly' | 'statusControl'
  // 注文詳細画面に商品追加への導線を表示するかどうか
  displayLinkToNewProductInOrder: boolean
  // 納品書に獲得予定ポイントを表示するかどうか
  displayEarnedPointInReceipt: boolean
  // バンドルミックスマッチ機能を表示するか
  showBundleMixmatch: boolean
  // 複数マスタアップロード機能が有効かどうか
  enableMultipleMasterDataUpload: boolean
  // 補償手続きを有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableCompensationClame: boolean
  // StailerTMSを有効にするかどうか
  // NOTE: 意味の実態はフレスタか否か程度のフラグ
  //  利用実態は管理画面でのピッキングリスト表示を制御しているのみ。
  //  StailerTMSを使っていない、DSSやヤマトで配送そもそも店舗受け渡ししかないパートナーであっても
  //  ピッキングリストは表示されないとピックパックができないので、ピッキングリスト表示のためにほぼtrueにする。
  //  フレスタだけが、StailerTMSを使っていないし、ピックパックがStailer外で起きるのでfalseになる。
  enableStailerTMS: boolean
  // 予約商品機能を有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableReservationProduct: boolean
  // 口座振替機能を有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableBankAccountTransfer: boolean
  // カタログ機能を有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableCatalog: boolean
  // 仕入れ機能を有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableProcurement: boolean
  // 配送表印刷機能（紙OpsのTMS）を有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableDeliveryVouchers: boolean
  // 定期便機能を有効にするかどうか (フレスタ固有機能でフレスタ以外では利用しない)
  enableRecurringOrder: boolean
  // 発信履歴機能を有効にするかどうか
  enableOutgoingCallHistory: boolean
  // お問い合わせ表示を有効にするかどうか
  // NOTE: ServiceSpecification移行は検討中
  enableContants: boolean
  // サインインに外部ID認証を利用するかどうか
  useExternalIdAuthentication: boolean
  // 事業者会員機能を有効にするかどうか
  enableBusinessCustomerFeature: boolean
  // DSS接続設定を表示するかどうか（service specにdssが含まれるパートナーのみtrue）
  isDssAllowed: boolean
  // ポイントキャンペーン機能を有効にするかどうか（spec.marketing.enablePointCampaign 由来）
  enablePointCampaign: boolean
  // Proz Answers接続設定を表示するかどうか。
  // Proz Answersは顧客からの問い合わせ対応ツールで、Zendeskの代わりに導入するパートナーのみtrue（現状はイオンリテールのみ）。
  // NOTE: サービス仕様の inquiry 型分類（StailerInquiry/ZendeskInquiry, inquiry.proto）には載せず、
  //   AR個社対応として config フラグで管理している。将来 inquiry の型区別を撤廃する方針があり、
  //   その際にお問い合わせ手段の管理方法を再検討する。ref: customer_support.proto の oneof inquiry コメント
  enableProzAnswers: boolean
}

export interface PartnerConfig {
  featureFlags: FeatureFlags
  // パートナー名
  name: string
  // 短縮したパートナー名
  shortName: string
  // 最後の操作からログアウトするまでの時間。プラットフォームの仕様としては原則3時間
  // NOTE: ServiceSpecificationには移行しない。共通デフォルトは原則3時間で、実質ライフのみが8時間に変更する例外（他パートナーは共通デフォルトのまま）。
  sessionTimeoutMs: number
  // ヤマトの配送システムを利用する店舗リスト
  // NOTE:  ServiceSpecification移行待ち
  useYamatoTransportServiceShopIds: Array<String>
  // 対応する販促企画。stailer-serverのsite_config#salesBadgesに対応。
  salesBadges: Array<SalesBadge>
  // 空文字にすると指定した注釈コメントを表示しない
  // NOTE: Point項目はspec由来だが、それ以外の個社文言はServiceSpecificationには移行しない（複数パートナーが個別に指定するopt-in）。
  customReceiptComments: Map<ReceiptComment, string> | null
  // 常に表示する注釈コメント
  // NOTE: ServiceSpecificationには移行しない。共通デフォルトはなしで、実質ライフのみが指定する例外（他パートナーは共通デフォルトのまま）。
  alwaysDisplayReceiptComments: Set<ReceiptComment> | null
}

// &で接続した複数のObject型をマージするユーティリティ型
type Merge<T> = { [K in keyof T]: T[K] }
// PartnerConfigのうち必須のプロパティのみを持つ型
type RequiredPartnerConfig = Pick<PartnerConfig, 'name' | 'shortName'>
// PartnerConfigのうち任意のプロパティのみを持つ型
type OptionalPartnerConfig = Merge<
  { featureFlags: Partial<FeatureFlags> } & Partial<Omit<PartnerConfig, 'featureFlags'>>
>

// default valueを使ってPartnerConfigを作成する
const createPartnerConfig = (c: RequiredPartnerConfig & OptionalPartnerConfig): PartnerConfig => {
  const f = c.featureFlags
  return {
    featureFlags: {
      enableCashOnDeliveryFee: f.enableCashOnDeliveryFee ?? false,
      enableEditAlcoholSalesManagementInfo: f.enableEditAlcoholSalesManagementInfo ?? false,
      enableSubscriptionV2: f.enableSubscriptionV2 ?? false,
      enableStailerStandardSalesFinalizer: f.enableStailerStandardSalesFinalizer ?? false,
      preferDayBeforePicking: f.preferDayBeforePicking ?? false,
      enableChangeDeliveryTime: f.enableChangeDeliveryTime ?? false,
      displayBarcodeInPickList: f.displayBarcodeInPickList ?? false,
      displayProductCodeInPickList: f.displayProductCodeInPickList ?? false,
      productCodeDisplayTypeInPickList: f.productCodeDisplayTypeInPickList ?? 'none',
      productCodeEmphasizedDigitInPickList: f.productCodeEmphasizedDigitInPickList ?? 0,
      productCodePositionInPickList: f.productCodePositionInPickList ?? 'productNameColumn',
      isDisplaySplitEarnedPoint: f.isDisplaySplitEarnedPoint ?? false,
      enableLinkPointInfo: f.enableLinkPointInfo ?? false,
      enableRedeemPoint: f.enableRedeemPoint ?? false,
      useInStoreArrangedDelivery: f.useInStoreArrangedDelivery ?? false,
      useAlternativePicking: f.useAlternativePicking ?? false,
      contactManagementType: f.contactManagementType ?? 'viewOnly',
      displayLinkToNewProductInOrder: f.displayLinkToNewProductInOrder ?? false,
      displayEarnedPointInReceipt: f.displayEarnedPointInReceipt ?? false,
      showBundleMixmatch: f.showBundleMixmatch ?? false,
      enableCompensationClame: f.enableCompensationClame ?? false,
      enableMultipleMasterDataUpload: f.enableMultipleMasterDataUpload ?? false,
      enableStailerTMS: f.enableStailerTMS ?? false,
      enableReservationProduct: f.enableReservationProduct ?? false,
      enableBankAccountTransfer: f.enableBankAccountTransfer ?? false,
      enableCatalog: f.enableCatalog ?? false,
      enableProcurement: f.enableProcurement ?? false,
      enableDeliveryVouchers: f.enableDeliveryVouchers ?? false,
      enableRecurringOrder: f.enableRecurringOrder ?? false,
      enableOutgoingCallHistory: f.enableOutgoingCallHistory ?? false,
      enableContants: f.enableContants ?? false,
      useExternalIdAuthentication: f.useExternalIdAuthentication ?? false,
      enableBusinessCustomerFeature: f.enableBusinessCustomerFeature ?? false,
      isDssAllowed: f.isDssAllowed ?? false,
      enablePointCampaign: f.enablePointCampaign ?? false,
      enableProzAnswers: f.enableProzAnswers ?? false,
    },
    name: c.name,
    shortName: c.shortName,
    sessionTimeoutMs: c.sessionTimeoutMs ?? 3 * 60 * 60 * 1000,
    useYamatoTransportServiceShopIds: c.useYamatoTransportServiceShopIds ?? [],
    salesBadges: c.salesBadges ?? [{ value: 0, label: 'なし' }],
    customReceiptComments: c.customReceiptComments ?? null,
    alwaysDisplayReceiptComments: c.alwaysDisplayReceiptComments ?? null,
  }
}

// siteTypeに基づいてPartnerConfigを返します。
export function getPartnerConfig(siteType: EcSite.SiteType): PartnerConfig {
  return createServiceSpecificationBackedPartnerConfig(siteType)(
    getHardCodedPartnerConfig(siteType),
  )
}

export function getHardCodedPartnerConfig(siteType: EcSite.SiteType): PartnerConfig {
  switch (siteType) {
    case EcSite.SiteType.FRESTA: {
      return createPartnerConfig({
        featureFlags: {
          enableSubscriptionV2: true,
          enableLinkPointInfo: true,
          displayEarnedPointInReceipt: true,
          enableCompensationClame: true,
          enableMultipleMasterDataUpload: true,
          enableReservationProduct: true,
          enableBankAccountTransfer: true,
          enableCatalog: true,
          enableProcurement: true,
          enableDeliveryVouchers: true,
          enableRecurringOrder: true,
          enableBusinessCustomerFeature: false,
        },
        name: 'エブリデイフレスタ',
        shortName: 'フレスタ',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 2, label: '日替わり' },
          { value: 3, label: '月安' },
        ],
      })
    }
    case EcSite.SiteType.LIFE: {
      return createPartnerConfig({
        featureFlags: {
          enableEditAlcoholSalesManagementInfo: true,
          enableStailerStandardSalesFinalizer: true,
          enableChangeDeliveryTime: true,
          displayProductCodeInPickList: true,
          productCodeDisplayTypeInPickList: 'scanCode',
          productCodeEmphasizedDigitInPickList: 4,
          isDisplaySplitEarnedPoint: true,
          enableLinkPointInfo: true,
          enableRedeemPoint: true,
          enableCashOnDeliveryFee: true,
          useInStoreArrangedDelivery: true,
          useAlternativePicking: true,
          contactManagementType: 'statusControl',
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          showBundleMixmatch: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: false,
          isDssAllowed: true,
        },
        name: 'ライフネットスーパー',
        shortName: 'ライフ',
        sessionTimeoutMs: 8 * 60 * 60 * 1000,
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 3, label: '定番' },
          { value: 4, label: '日替わり' },
          { value: 5, label: '広告の品' },
          { value: 6, label: 'おすすめ' },
        ],
        alwaysDisplayReceiptComments: new Set([ReceiptComment.SelfMedication]),
      })
    }
    case EcSite.SiteType.DEMO: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          displayBarcodeInPickList: true,
          enableLinkPointInfo: false,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'Stailerネットスーパー',
        shortName: 'Stailer',
      })
    }
    case EcSite.SiteType.ALBIS: {
      return createPartnerConfig({
        featureFlags: {
          displayBarcodeInPickList: true,
          productCodeEmphasizedDigitInPickList: 3,
          enableLinkPointInfo: true,
          displayEarnedPointInReceipt: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'アルビスネットスーパー',
        shortName: 'アルビス',
      })
    }
    case EcSite.SiteType.YAKUODO: {
      return createPartnerConfig({
        featureFlags: {
          displayBarcodeInPickList: true,
          productCodeEmphasizedDigitInPickList: 3,
          enableLinkPointInfo: true,
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
        },
        name: '薬王堂 P!ck and',
        shortName: '薬王堂',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: '激得' },
          { value: 2, label: '広告の品' },
        ],
        customReceiptComments: new Map([
          [
            ReceiptComment.ContactInfo,
            'お問い合わせ先：P!ck andのお問い合わせフォームよりご連絡ください。',
          ],
        ]),
      })
    }
    case EcSite.SiteType.HEIWADO: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          displayBarcodeInPickList: true,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
        },
        name: '平和堂ネットスーパー',
        shortName: '平和堂',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: 'おすすめ' },
          { value: 2, label: 'ハトオシ' },
        ],
        customReceiptComments: new Map([
          [
            ReceiptComment.ContactInfo,
            'お問い合わせ先：商品についてはご購入店舗へ、アプリの不具合等についてはネットスーパーアプリお問い合わせフォームよりご連絡ください。',
          ],
        ]),
      })
    }
    case EcSite.SiteType.SUGI: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          displayBarcodeInPickList: true,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          productCodeEmphasizedDigitInPickList: 4,
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: false,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'スギスマホオーダー',
        shortName: 'スギ薬局',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: '期間特売' },
          { value: 2, label: '広告の品' },
        ],
        customReceiptComments: new Map([
          [
            ReceiptComment.ContactInfo,
            'お問い合わせ先：スギスマホオーダーのお問い合わせフォームよりご連絡ください。',
          ],
        ]),
      })
    }
    case EcSite.SiteType.DELICIA: {
      return createPartnerConfig({
        featureFlags: {
          enableCashOnDeliveryFee: true,
          enableStailerStandardSalesFinalizer: true,
          preferDayBeforePicking: true,
          displayBarcodeInPickList: true,
          productCodeEmphasizedDigitInPickList: 4,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'デリシア ネットスーパー',
        shortName: 'デリシア',
        useYamatoTransportServiceShopIds: [
          '23', // 寿店
          '82', // 篠ノ井店
          '410', // 豊野店
          '411', // 松本広域ネットスーパー広丘店
        ],
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: '広告の品' },
        ],
      })
    }
    case EcSite.SiteType.LADYDRUG: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          preferDayBeforePicking: true,
          displayBarcodeInPickList: true,
          enableLinkPointInfo: true,
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'レデイのぽちっとオーダー',
        shortName: 'レデイ薬局',
        customReceiptComments: new Map([
          [ReceiptComment.Point, '※ ポイント加算はお届け日の数日後です。'],
          [
            ReceiptComment.ContactInfo,
            'お問い合わせ先：レデイのポチッとオーダーアプリのお問い合わせフォームよりご連絡ください。',
          ],
        ]),
      })
    }
    case EcSite.SiteType.OGINO: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          preferDayBeforePicking: true,
          displayBarcodeInPickList: true,
          isDisplaySplitEarnedPoint: false,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'オギノネットスーパー',
        shortName: 'オギノ',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: 'ネット限定' },
          { value: 2, label: '広告の品' },
          { value: 3, label: 'おすすめ' },
          { value: 4, label: 'パワープライス' },
          { value: 5, label: 'オギノセレクトプレミアム' },
          { value: 6, label: 'オギノセレクト' },
        ],
      })
    }
    case EcSite.SiteType.TORISEN: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          displayBarcodeInPickList: true,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'とりせんネットスーパー',
        shortName: 'とりせん',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: 'ネット限定' },
          { value: 2, label: '広告の品' },
          { value: 3, label: 'いち推し' },
        ],
      })
    }
    case EcSite.SiteType.SEISENICHIBATOP: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          preferDayBeforePicking: true,
          displayBarcodeInPickList: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
        },
        name: '生鮮市場TOP！ネットスーパー',
        shortName: '生鮮市場TOP！',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: '広告の品' },
          { value: 2, label: '日替わり' },
        ],
      })
    }
    case EcSite.SiteType.ARUK: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          preferDayBeforePicking: true,
          displayBarcodeInPickList: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
        },
        name: 'アルクネットスーパー',
        shortName: 'アルク',
      })
    }
    case EcSite.SiteType.FUJI: {
      return createPartnerConfig({
        featureFlags: {
          enableCashOnDeliveryFee: true,
          enableStailerStandardSalesFinalizer: true,
          displayBarcodeInPickList: true,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          enableMultipleMasterDataUpload: true,
          enableChangeDeliveryTime: true,
          enableStailerTMS: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
        },
        name: 'フジネットスーパーおまかせくん',
        shortName: 'フジ',
      })
    }
    case EcSite.SiteType.SHIZUTETSUSTORE: {
      return createPartnerConfig({
        featureFlags: {
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayLinkToNewProductInOrder: true,
          displayBarcodeInPickList: true,
          enableStailerStandardSalesFinalizer: true,
          enableChangeDeliveryTime: true,
          enableOutgoingCallHistory: true,
          enableStailerTMS: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
        },
        name: 'しずてつストアネットスーパー',
        shortName: 'しずてつストア',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: 'おすすめ' },
          { value: 2, label: 'いい値チョイス' },
          { value: 3, label: '得々の市' },
        ],
        customReceiptComments: new Map([
          [ReceiptComment.Point, '※ ポイントの加算は、商品お届け日の2~3日後を予定しています。'],
        ]),
      })
    }
    case EcSite.SiteType.AEONRETAIL: {
      return createPartnerConfig({
        featureFlags: {
          enableCashOnDeliveryFee: true,
          enableLinkPointInfo: true,
          useAlternativePicking: true,
          displayProductCodeInPickList: true,
          productCodePositionInPickList: 'productCodeColumn',
          productCodeDisplayTypeInPickList: 'scanCode',
          displayLinkToNewProductInOrder: true,
          displayEarnedPointInReceipt: true,
          enableStailerStandardSalesFinalizer: true,
          enableChangeDeliveryTime: true,
          // 「お客様関連 > お問い合わせ」はZendeskからのお問い合わせを閲覧する画面への導線。ARではZendeskの代わりにProz Answersで問い合わせ対応するため非表示にする
          enableContants: false,
          useExternalIdAuthentication: true,
          enableStailerTMS: true,
          enableOutgoingCallHistory: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
          // AR個社対応。詳細は FeatureFlags.enableProzAnswers の定義コメント参照
          enableProzAnswers: true,
        },
        name: 'おうちでイオン イオンネットスーパー',
        shortName: 'イオンリテール',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: '生活応援' },
          { value: 2, label: '新商品' },
          { value: 3, label: 'おすすめ品' },
          { value: 4, label: 'お得値まつり' },
        ],
        customReceiptComments: new Map([
          [
            ReceiptComment.ContactInfo,
            'お問い合わせ先：【ネットスーパーアプリ】のお問い合わせフォームよりご連絡ください。',
          ],
        ]),
      })
    }
    case EcSite.SiteType.MATSUYA: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerStandardSalesFinalizer: true,
          displayLinkToNewProductInOrder: true,
          enableChangeDeliveryTime: true,
          enableOutgoingCallHistory: false,
          enableContants: true,
          enableStailerTMS: true,
          displayBarcodeInPickList: false,
          displayProductCodeInPickList: true,
          productCodeDisplayTypeInPickList: 'scanCode',
          productCodePositionInPickList: 'productNameColumn',
          productCodeEmphasizedDigitInPickList: 4,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
          useAlternativePicking: true,
        },
        name: 'マツヤネットスーパー',
        shortName: 'マツヤ',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: 'おすすめ' },
          { value: 2, label: '日替わり' },
          { value: 3, label: 'お買い得商品' },
          { value: 4, label: 'ネット限定' },
          { value: 5, label: '広告の品' },
        ],
        customReceiptComments: new Map([
          [
            ReceiptComment.ContactInfo,
            'お問い合わせ先：マツヤネットスーパーアプリのお問い合わせフォームよりご連絡ください。',
          ],
        ]),
      })
    }
    case EcSite.SiteType.STNET: {
      return createPartnerConfig({
        featureFlags: {
          enableStailerTMS: true,
          enableLinkPointInfo: true,
          displayBarcodeInPickList: true,
          displayLinkToNewProductInOrder: true,
          preferDayBeforePicking: true,
          enableStailerStandardSalesFinalizer: true,
          enableChangeDeliveryTime: true,
          enableOutgoingCallHistory: true,
          enableContants: true,
          enableBusinessCustomerFeature: true,
          isDssAllowed: true,
        },
        name: 'トライアルネットスーパー',
        shortName: 'トライアル',
        salesBadges: [
          { value: 0, label: 'なし' },
          { value: 1, label: 'TRIALオリジナル' },
          { value: 2, label: 'エキサイティングプライス' },
          { value: 3, label: '総力祭' },
          { value: 4, label: '感謝祭' },
        ],
      })
    }
    default: {
      return createPartnerConfig({ featureFlags: {}, name: '', shortName: '' })
    }
  }
}

// ServiceSpecificationに基づいてFeatureFlagsを作成する関数を返します。
// fallbackの値をベースに、Specから読み取れた値だけを上書きします。
// Specに定義のないフィールドはfallback（ハードコード値）がそのまま使われます。
function createServiceSpecificationBackedFeatureFlags(
  spec: ServiceSpecification,
  siteType: EcSite.SiteType,
) {
  return (fallback: FeatureFlags): FeatureFlags => {
    return {
      ...fallback,
      ...createPartnerConfigFeatureFlagsPickPackPart(spec),
      ...createPartnerConfigFeatureFlagsDeliveryMethodPart(spec),
      ...createPartnerConfigFeatureFlagsSalesReportPart(spec),
      ...createPartnerConfigFeatureFlagsAccountPart(spec),
      ...createPartnerConfigFeatureFlagsMerchandisingPart(spec),
      ...createPartnerConfigFeatureFlagsMarketingPart(spec),
      ...createPartnerConfigFeatureFlagsPaymentPart(spec),
      ...createPartnerConfigFeatureFlagsFrestaPart(siteType),
      ...createPartnerConfigFeatureFlagsCustomerSupportPart(spec),
      ...createPartnerConfigFeatureFlagsCustomerOrderPart(spec),
    }
  }
}

// ServiceSpecificationのsalesBadgesをPartnerConfigのSalesBadge配列に変換します。
// badge_id: 0 は「なし」として予約済みのため除外し、先頭に { value: 0, label: 'なし' } を付加します。
// Spec内の定義順は不定のため、badgeId昇順にソートして出力順を安定させます。
// @visibleForTesting
export function convertSpecSalesBadges(
  specSalesBadges: ReadonlyArray<{ badgeId: number; label: string }>,
): Array<SalesBadge> {
  return [
    { value: 0, label: 'なし' },
    ...specSalesBadges
      .filter((b) => b.badgeId !== 0)
      .sort((a, b) => a.badgeId - b.badgeId)
      .map((b) => ({ value: b.badgeId, label: b.label })),
  ]
}

// ServiceSpecificationに基づいてPartnerConfigを作成する関数を返します。
// 戻り値はfallback => PartnerConfigな関数です。呼び出し元がハードコードのconfigを渡すと、
// ServiceSpecから読み取った値で上書きしたconfigを返します。
// Specが存在しないsiteTypeは恒等関数を返し、ハードコード値がそのまま使われます。
export function createServiceSpecificationBackedPartnerConfig(siteType: EcSite.SiteType) {
  const specificationJson = (() => {
    switch (siteType) {
      case EcSite.SiteType.LIFE:
        return specificationJsonProvider.specificationJsons['life_service_specification.json']
      case EcSite.SiteType.ALBIS:
        return specificationJsonProvider.specificationJsons['albis_service_specification.json']
      case EcSite.SiteType.FUJI:
        return specificationJsonProvider.specificationJsons['fuji_service_specification.json']
      case EcSite.SiteType.DEMO:
        return specificationJsonProvider.specificationJsons['demo_service_specification.json']
      case EcSite.SiteType.HEIWADO:
        return specificationJsonProvider.specificationJsons['heiwado_service_specification.json']
      case EcSite.SiteType.SUGI:
        return specificationJsonProvider.specificationJsons['sugi_service_specification.json']
      case EcSite.SiteType.DELICIA:
        return specificationJsonProvider.specificationJsons['delicia_service_specification.json']
      case EcSite.SiteType.OGINO:
        return specificationJsonProvider.specificationJsons['ogino_service_specification.json']
      case EcSite.SiteType.TORISEN:
        return specificationJsonProvider.specificationJsons['torisen_service_specification.json']
      case EcSite.SiteType.SEISENICHIBATOP:
        return specificationJsonProvider.specificationJsons[
          'seisenichibatop_service_specification.json'
        ]
      case EcSite.SiteType.ARUK:
        return specificationJsonProvider.specificationJsons['aruk_service_specification.json']
      case EcSite.SiteType.SHIZUTETSUSTORE:
        return specificationJsonProvider.specificationJsons[
          'shizutetsustore_service_specification.json'
        ]
      case EcSite.SiteType.AEONRETAIL:
        return specificationJsonProvider.specificationJsons['aeonretail_service_specification.json']
      case EcSite.SiteType.MATSUYA:
        return specificationJsonProvider.specificationJsons['matsuya_service_specification.json']
      case EcSite.SiteType.FRESTA:
        return specificationJsonProvider.specificationJsons['fresta_service_specification.json']
      case EcSite.SiteType.YAKUODO:
        return specificationJsonProvider.specificationJsons['yakuodo_service_specification.json']
      case EcSite.SiteType.STNET:
        return specificationJsonProvider.specificationJsons['stnet_service_specification.json']
      default:
        return undefined
    }
  })()

  if (!specificationJson) {
    return (fallback: PartnerConfig): PartnerConfig => fallback
  }

  /// NOTE: fromJsonの型推論がうまく働かないため、明示的に型アサーションを行う
  const spec = fromJson<typeof ServiceSpecificationSchema>(
    ServiceSpecificationSchema,
    specificationJson as unknown /* ドデカJSONの型が解決できないのでunknownにしている  */ as JsonValue,
  ) as ServiceSpecification
  return ({ name, shortName, ...fallback }: PartnerConfig): PartnerConfig => {
    const featureFlags = createServiceSpecificationBackedFeatureFlags(
      spec,
      siteType,
    )(fallback.featureFlags)
    const salesBadges = convertSpecSalesBadges(spec.marketing?.salesBadges ?? [])
    const customReceiptComments = deriveCustomReceiptCommentsFromSpec(
      spec,
      fallback.customReceiptComments,
    )
    return {
      ...fallback,
      featureFlags,
      salesBadges,
      name: spec.service?.name ?? name,
      shortName: spec.service?.shortName ?? shortName,
      customReceiptComments,
    }
  }
}

// ServiceSpecificationからcustomReceiptCommentsのPoint項目を導出します。
// Specで設定がある場合はSpecの値を優先し、それ以外のキーはfallbackの値を保持します。
// @visibleForTesting
export function deriveCustomReceiptCommentsFromSpec(
  spec: ServiceSpecification,
  fallback: Map<ReceiptComment, string> | null,
): Map<ReceiptComment, string> | null {
  const pointCard = spec.payment?.pointCardSetting
  if (pointCard?.case !== 'enablePointCard') {
    return fallback
  }

  const earnedPointInReceipt = pointCard.value.earnedPointInReceipt
  if (earnedPointInReceipt.case !== 'enableEarnedPointInReceipt') {
    return fallback
  }

  const commentSetting = earnedPointInReceipt.value.receiptEarnedPointComment
  if (commentSetting.case !== 'customComment') {
    // useDefaultCommentの場合はcustomReceiptCommentsに追加不要
    // （納品書表示時にReceiptComment.Pointのenum値がデフォルトで使われるため）
    return fallback
  }
  const result = new Map(fallback ?? [])
  result.set(ReceiptComment.Point, commentSetting.value)
  return result
}
